import { useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ApiError } from "@/lib/api";
import type { Membership, Site } from "@sitebrew/contracts/v1";
import { looksLikeSlug, slugFrom, useCreateSite } from "@/lib/sites";
import { BusyButton } from "@/components/button";
import { Notice } from "@/components/ui/login-card";
import { FieldError, Input, Label } from "@/components/ui/field";

const schema = z.object({
  name: z.string().trim().min(1, "Give the site a name").max(200, "That name is too long"),
  slug: z
    .string()
    .trim()
    .min(1, "Choose an address")
    /*
     * The DNS label's own limit, said as a length rather than as the shape
     * message. `looksLikeSlug` already rejects a 64th character — the pattern
     * caps it, as the API's does — but it would answer "use a–z, 0–9 and
     * hyphens" to somebody whose address is made of exactly those and merely
     * too long, which is the console blaming the wrong thing.
     */
    .max(63, "An address can be at most 63 characters")
    /*
     * Shape only, and the API decides for real — the same division the sign-in
     * screen makes about email addresses. Reserved names and names already
     * taken are not knowable here, and the answers to both are shown verbatim
     * below rather than guessed at.
     */
    .refine(looksLikeSlug, "Use a–z, 0–9 and hyphens, starting and ending with a letter or digit"),
});

type Values = z.infer<typeof schema>;

/** The refusals that are about the address field, rather than about the request. */
const ABOUT_THE_SLUG = new Set(["invalid_slug", "slug_taken", "slug_retired"]);

/**
 * Create a site: the form, and the state the dialog around it needs.
 *
 * Split in two because `FormDialog` asks before throwing away typed text and
 * refuses to close mid-request, and both answers live in `formState` — so the
 * form instance is made here and handed to the dialog, while the fields below
 * are only fields.
 *
 * Two of them, because the API takes two: a name for people and a slug that
 * becomes the site's hostname. The address is *not* previewed as
 * `<slug>.something` — the domain lives in the API's configuration and differs
 * per stage, so a console that spelled one out would be guessing on the one
 * screen where the person is deciding what to type. The created site carries
 * its real hostname back, and that is where it is first shown.
 *
 * The submit is disabled for the whole mutation, as on the sign-in screen: a
 * create that arrives twice is not idempotent from the person's side — the
 * second one is either "slug taken" by their own first attempt or, if the first
 * had not finished, a resume they did not ask for (§9).
 */
export function useNewSiteForm(workspace: Membership, onCreated: (site: Site) => void) {
  const create = useCreateSite(workspace.id);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", slug: "" },
  });

  const failure = create.error instanceof ApiError ? create.error : null;
  const slugFailure = failure && ABOUT_THE_SLUG.has(failure.code ?? "") ? failure : null;

  /*
   * Whether the address has been taken over by hand.
   *
   * A ref rather than `formState.dirtyFields.slug`, which is what this used to
   * read. That field is computed lazily: react-hook-form only maintains it
   * while something subscribes to it, so the guard was answering "not edited"
   * to every keystroke — until the dialog around this form subscribed to
   * `isDirty` for its own reasons and the suggestion stopped after the first
   * letter. A behaviour that depends on which parts of `formState` some other
   * component happens to read is not a behaviour; this is the fact itself.
   */
  const addressEdited = useRef(false);

  return {
    form,
    create,
    addressEdited,
    slugFailure,
    requestFailure: failure && !slugFailure ? failure : null,
    submit: form.handleSubmit(async (values) => {
      const site = await create.mutateAsync(values).catch(() => null);
      // A refusal is rendered in the dialog; only a created site moves on.
      if (site) onCreated(site);
    }),
  };
}

export type NewSiteForm = ReturnType<typeof useNewSiteForm>;

export function NewSiteFields({
  form,
  create,
  addressEdited,
  slugFailure,
  requestFailure,
  submit,
}: NewSiteForm) {
  return (
    <>
      {requestFailure ? (
        /*
         * The API's own sentence, not a paraphrase. Each of these says
         * something the person has to act on differently — a stage without a
         * GitHub App cannot create anything (`503`), while a repository that
         * failed halfway is resumed by submitting again (`502`) — and the API
         * is where that difference is decided (§6).
         */
        <Notice className="mb-5">{requestFailure.message}</Notice>
      ) : null}

      <form noValidate onSubmit={submit}>
        <Label htmlFor="site-name">Name</Label>
        <Input
          id="site-name"
          autoFocus
          autoComplete="off"
          placeholder="Acme Bakery"
          aria-invalid={form.formState.errors.name ? true : undefined}
          {...form.register("name", {
            // The address follows the name until somebody edits it; see the
            // ref above for why that is remembered rather than asked of
            // `formState`.
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              if (addressEdited.current) return;
              form.setValue("slug", slugFrom(event.target.value), { shouldDirty: false });
            },
          })}
        />
        <FieldError>{form.formState.errors.name?.message}</FieldError>

        <div className="mt-[.9rem]">
          <Label htmlFor="site-slug">Address</Label>
          <Input
            id="site-slug"
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="acme-bakery"
            aria-invalid={form.formState.errors.slug || slugFailure ? true : undefined}
            {...form.register("slug", {
              // Typed into by hand, so it is theirs from here — including when
              // what they typed is nothing. A person clearing the field is
              // editing it, and having the name type over them again would be
              // the console arguing with its user.
              onChange: () => {
                addressEdited.current = true;
              },
            })}
          />
          <FieldError>{form.formState.errors.slug?.message ?? slugFailure?.message}</FieldError>
        </div>

        <BusyButton className="mt-[.9rem] w-full" type="submit" busy={create.isPending} busyLabel="Creating…">
          Create site
        </BusyButton>
      </form>
    </>
  );
}
